/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable quotes */
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./style.scss";
import {
  Row,
  Col,
  Button,
  message,
  Card,
  Space,
  Typography,
  Tag,
  Upload,
  Divider,
  Form,
  Input,
  Select,
  Modal,
  Spin,
  Checkbox,
  Table,
} from "antd";
import API from "../../../api";
import urls from "../../../api/urls";
import {
  UploadOutlined,
  ScanOutlined,
  FileImageOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  GroupOutlined,
  FileAddOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
import { getUserAndPremiseId } from "../../../utils";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const DeliveredPODUpload = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Get data from navigation state
  const {
    selectedShipments = [],
    status = "delivered",
    receiverName = "",
    receiverPhone = "",
    remarks = "",
    isBulkUpdate = false,
  } = location.state || {};

  // State management
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [shipmentGroups, setShipmentGroups] = useState([]);
  const [groupFiles, setGroupFiles] = useState({}); // {groupId: {file: File, url: string, s3Url: string}}
  const [uploadingFiles, setUploadingFiles] = useState({}); // {groupId: boolean}
  const [geolocation, setGeolocation] = useState(null);
  const [selectedGroupRows, setSelectedGroupRows] = useState([]);
  const [groupingMode, setGroupingMode] = useState(false);
  const [groupReceiverNames, setGroupReceiverNames] = useState({}); // {groupId: receiverName}
  const [bulkReceiverName, setBulkReceiverName] = useState("");

  // State for POD upload modal
  const [podModalVisible, setPodModalVisible] = useState(false);
  const [currentGroupForPod, setCurrentGroupForPod] = useState(null);
  const [podModalForm] = Form.useForm();
  const [podModalFile, setPodModalFile] = useState(null);
  const [podModalUploading, setPodModalUploading] = useState(false);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeolocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Error getting location:", error);
          // Set default coordinates if location access is denied
          setGeolocation({
            latitude: 0,
            longitude: 0,
          });
        }
      );
    } else {
      setGeolocation({
        latitude: 0,
        longitude: 0,
      });
    }
  }, []);

  // Initialize component
  useEffect(() => {
    if (!selectedShipments || selectedShipments.length === 0) {
      message.error("No shipments selected. Please go back and select shipments.");
      navigate(-1);
      return;
    }

    // Initialize shipment groups - each shipment starts as its own group
    const initialGroups = selectedShipments.map((shipment, index) => ({
      id: `group-${index}`,
      awbNumbers: [shipment.awbNumber || shipment.cAWB_No],
      shipments: [shipment],
      isGroup: false,
    }));

    setShipmentGroups(initialGroups);
    getCurrentLocation();

    // Pre-fill form with values from previous modal
    form.setFieldsValue({
      remarks: remarks,
    });
  }, [selectedShipments, navigate, getCurrentLocation, receiverName, receiverPhone, remarks, form]);

  // Upload file to server and get S3 URL
  const uploadFileToServer = async (file, fileName) => {
    const formData = new FormData();

    const fileExtension = file.name.split(".").pop().toLowerCase();
    const uploadFileName = `${fileName}.${fileExtension}`;

    // Append file with proper structure for DRS POD upload
    formData.append("files", file, uploadFileName);
    formData.append("path", "/drs_pod");
    formData.append("fileType", file.type);
    formData.append("filename", uploadFileName);

    const response = await API.Api(urls.DRS_UPLOAD_POD).post(formData);

    if (response.status && response.response?.data?.data?.[0]?.url) {
      return response.response.data.data[0].url;
    } else {
      throw new Error("Failed to upload file to server");
    }
  };

  // Handle file upload for a group
  const handleGroupFileUpload = async (groupId, file) => {
    try {
      setUploadingFiles((prev) => ({ ...prev, [groupId]: true }));

      const group = shipmentGroups.find(g => g.id === groupId);
      const fileName = `${group.awbNumbers[0]}-${Date.now()}`;

      // Upload to S3 first
      const s3Url = await uploadFileToServer(file, fileName);

      // Store both local preview and S3 URL
      const url = URL.createObjectURL(file);
      setGroupFiles((prev) => ({
        ...prev,
        [groupId]: {
          file,
          url,
          s3Url,
          type: file.type.includes("pdf") ? "pdf" : "image",
        },
      }));

      message.success(`POD uploaded for ${group.isGroup ? 'group' : 'shipment'} successfully`);
    } catch (error) {
      message.error(`Failed to upload POD: ${error.message}`);
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [groupId]: false }));
    }
  };

  // Remove file from group
  const removeGroupFile = (groupId) => {
    setGroupFiles((prev) => {
      const updated = { ...prev };
      if (updated[groupId]?.url) {
        URL.revokeObjectURL(updated[groupId].url);
      }
      delete updated[groupId];
      return updated;
    });

    message.success("File removed successfully");
  };

  // Handle opening POD upload modal
  const handleOpenPodModal = (group) => {
    setCurrentGroupForPod(group);
    setPodModalVisible(true);
    podModalForm.setFieldsValue({
      receiverName: groupReceiverNames[group.id] || "",
    });
  };

  // Handle POD modal file selection
  const handlePodModalFileChange = (file) => {
    setPodModalFile(file);
    return false; // Prevent auto upload
  };

  // Handle POD modal form submission
  const handlePodModalSubmit = async () => {
    try {
      setPodModalUploading(true);

      // Validate form
      const values = await podModalForm.validateFields();

      if (!podModalFile) {
        message.error("Please select a POD file to upload");
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (podModalFile.size > maxSize) {
        message.error("File is too large. Please select a file smaller than 10MB");
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(podModalFile.type)) {
        message.error("Invalid file type. Please select an image (JPEG, PNG, GIF, WebP) or PDF file");
        return;
      }

      // Upload file using existing uploadFileToServer function
      const fileName = `${currentGroupForPod.awbNumbers[0]}-${Date.now()}`;

      const s3Url = await uploadFileToServer(podModalFile, fileName);

      // Update group files
      setGroupFiles(prev => ({
        ...prev,
        [currentGroupForPod.id]: {
          file: podModalFile,
          url: URL.createObjectURL(podModalFile),
          s3Url: s3Url,
          type: podModalFile.type.includes("pdf") ? "pdf" : "image",
        }
      }));

      // Update receiver name
      setGroupReceiverNames(prev => ({
        ...prev,
        [currentGroupForPod.id]: values.receiverName
      }));

      message.success("POD uploaded and receiver name saved successfully!");

      // Close modal and reset
      setPodModalVisible(false);
      setPodModalFile(null);
      podModalForm.resetFields();
      setCurrentGroupForPod(null);
    } catch (error) {
      console.error("POD modal submission error:", {
        error: error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Provide user-friendly error messages
      let userMessage = "Failed to upload POD";

      if (error.response?.status === 413) {
        userMessage = "File is too large. Please select a smaller file (max 10MB)";
      } else if (error.response?.status === 415) {
        userMessage = "File type not supported. Please select an image or PDF file";
      } else if (error.response?.status >= 500) {
        userMessage = "Server error. Please try again later";
      } else if (error.message?.includes("Network Error")) {
        userMessage = "Network error. Please check your connection and try again";
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      }

      message.error(userMessage);
    } finally {
      setPodModalUploading(false);
    }
  };

  // Handle POD modal cancel
  const handlePodModalCancel = () => {
    setPodModalVisible(false);
    setPodModalFile(null);
    podModalForm.resetFields();
    setCurrentGroupForPod(null);
  };

  // Handle removing a shipment from the list
  const handleRemoveShipment = (groupId) => {
    const updatedGroups = shipmentGroups.filter(group => group.id !== groupId);

    // Clean up associated data
    const updatedGroupFiles = { ...groupFiles };
    const updatedGroupReceiverNames = { ...groupReceiverNames };

    if (updatedGroupFiles[groupId]) {
      if (updatedGroupFiles[groupId].url) {
        URL.revokeObjectURL(updatedGroupFiles[groupId].url);
      }
      delete updatedGroupFiles[groupId];
    }
    delete updatedGroupReceiverNames[groupId];

    setGroupFiles(updatedGroupFiles);
    setGroupReceiverNames(updatedGroupReceiverNames);
    setShipmentGroups(updatedGroups);

    // Remove from selected rows if it was selected
    setSelectedGroupRows(prev => prev.filter(id => id !== groupId));

    message.success("Shipment removed successfully");

    // If no shipments left, show a warning
    if (updatedGroups.length === 0) {
      Modal.warning({
        title: "No Shipments Remaining",
        content: "All shipments have been removed. You can go back to select different shipments or add more shipments.",
        okText: "Go Back",
        onOk: () => navigate(-1),
      });
    }
  };

  // Handle clear all receiver names
  const handleClearReceiverNames = () => {
    setGroupReceiverNames({});
    setBulkReceiverName("");
    message.success("All receiver names cleared");
  };

  // Handle bulk apply receiver name to all groups
  const handleBulkApplyReceiverName = () => {
    if (!bulkReceiverName.trim()) {
      message.warning("Please enter a receiver name to apply to all groups");
      return;
    }

    const updatedGroupReceiverNames = {};
    shipmentGroups.forEach(group => {
      updatedGroupReceiverNames[group.id] = bulkReceiverName.trim();
    });

    setGroupReceiverNames(updatedGroupReceiverNames);
    message.success(`Applied "${bulkReceiverName.trim()}" to all ${shipmentGroups.length} groups`);
  };

  // Handle close grouping - clear selections
  const handleCloseGrouping = () => {
    setGroupingMode(false);
    setSelectedGroupRows([]);
  };

  // Handle open grouping
  const handleOpenGrouping = () => {
    setGroupingMode(true);
  };

  // Handle grouping of selected shipments
  const handleCreateGroup = () => {
    if (selectedGroupRows.length < 2) {
      message.warning("Please select at least 2 shipments to create a group");
      return;
    }

    // Get selected groups
    const selectedGroups = shipmentGroups.filter(group =>
      selectedGroupRows.includes(group.id)
    );

    // Combine AWB numbers and shipments
    const combinedAwbNumbers = [];
    const combinedShipments = [];
    selectedGroups.forEach(group => {
      combinedAwbNumbers.push(...group.awbNumbers);
      combinedShipments.push(...group.shipments);
    });

    // Create new group
    const newGroupId = `group-${Date.now()}`;
    const newGroup = {
      id: newGroupId,
      awbNumbers: combinedAwbNumbers,
      shipments: combinedShipments,
      isGroup: true,
    };

    // Remove selected groups and add new combined group
    const remainingGroups = shipmentGroups.filter(group =>
      !selectedGroupRows.includes(group.id)
    );

    // Clean up receiver names for removed groups and files
    const updatedGroupReceiverNames = { ...groupReceiverNames };
    selectedGroupRows.forEach(groupId => {
      delete updatedGroupReceiverNames[groupId];
    });
    setGroupReceiverNames(updatedGroupReceiverNames);

    setShipmentGroups([...remainingGroups, newGroup]);
    setSelectedGroupRows([]);
    setGroupingMode(false);

    message.success(`Created group with ${combinedAwbNumbers.length} shipments`);
  };

  // Handle ungrouping
  const handleUngroupShipments = (groupId) => {
    const group = shipmentGroups.find(g => g.id === groupId);
    if (!group || !group.isGroup) return;

    // Remove the group file if exists
    if (groupFiles[groupId]) {
      removeGroupFile(groupId);
    }

    // Remove receiver name for this group
    const updatedGroupReceiverNames = { ...groupReceiverNames };
    delete updatedGroupReceiverNames[groupId];
    setGroupReceiverNames(updatedGroupReceiverNames);

    // Create individual groups for each shipment
    const individualGroups = group.shipments.map((shipment, index) => ({
      id: `${groupId}-split-${index}`,
      awbNumbers: [shipment.awbNumber || shipment.cAWB_No],
      shipments: [shipment],
      isGroup: false,
    }));

    // Replace the group with individual groups
    const updatedGroups = shipmentGroups.filter(g => g.id !== groupId);
    setShipmentGroups([...updatedGroups, ...individualGroups]);

    message.success("Group split into individual shipments");
  };

  // Generate API payload for exceptions endpoint
  const generateExceptionsPayload = () => {
    // Get user info for user_id
    const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
    const userId = userinfo?.userId || userinfo?.id;
    const userInfo = getUserAndPremiseId();
    const userName = `${userInfo?.firstName || ""} ${userInfo?.lastName || ""}`.trim();

    // Prepare shipment groups for API
    const apiShipmentGroups = shipmentGroups.map(group => {
      const groupData = {
        receiver_name: groupReceiverNames[group.id] || "",
        awb_numbers: group.awbNumbers,
      };

      // Add POD URL if available for this group
      if (groupFiles[group.id]?.s3Url) {
        groupData.pod_url = groupFiles[group.id].s3Url;
      }

      return groupData;
    });

    return {
      user_id: userId,
      userName: userName, // Added
      status: status,
      source: "NBA_WEB",
      event_timestamp: new Date().toISOString(),
      location: geolocation || { latitude: 0, longitude: 0 },
      details: {
        remarks: form.getFieldValue("remarks") || remarks,
        shipment_groups: apiShipmentGroups,
      },
    };
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      // Validate form fields
      await form.validateFields();

      // Check if there are any shipments left
      if (shipmentGroups.length === 0) {
        message.error("No shipments available for delivery. Please add shipments or go back.");
        return;
      }

      // Validate that all groups have POD files
      const groupsWithoutFiles = shipmentGroups.filter(
        (group) => !groupFiles[group.id]?.s3Url
      );

      if (groupsWithoutFiles.length > 0) {
        message.error("Please upload POD files for all shipments/groups");
        return;
      }

      // Validate that all groups have receiver names
      const groupsWithoutReceiverNames = shipmentGroups.filter(
        (group) => !groupReceiverNames[group.id]?.trim()
      );

      if (groupsWithoutReceiverNames.length > 0) {
        message.error("Please enter receiver name for all shipments/groups");
        return;
      }

      // Generate API payload
      const payload = generateExceptionsPayload();

      // Make API call to exceptions endpoint
      const response = await API.Api(urls.DRS_ORDER_EXCEPTIONS).post(payload);

      // Check for successful response
      // Note: HTTP status might be 201, but API can return error in response body
      const apiData = response.response?.data;
      const httpStatus = response.response?.status;
      const apiStatus = apiData?.status;

      if (
        response.status &&
        httpStatus >= 200 &&
        httpStatus < 300 &&
        (!apiStatus || (apiStatus >= 200 && apiStatus < 300)) &&  // Check API internal status
        apiData?.success !== false
      ) {
        message.success("Shipments delivered successfully with POD!");

        // Navigate back to status updates page
        navigate("/rto-shipments", {
          state: {
            refresh: true,
            message: "Delivered status updated successfully"
          }
        });
      } else {
        // Handle API error responses
        // API returned error status in response body (e.g., status: 400 inside 201 response)
        if (apiData) {
          const error = new Error(apiData.message || "Failed to update delivery status");
          error.response = { data: apiData };
          throw error;
        } else {
          throw new Error("Failed to update delivery status");
        }
      }
    } catch (error) {
      console.error("Submission error:", error);

      // Handle specific error responses
      if (error.response?.data) {
        const errorData = error.response.data;

        // Handle damaged shipments error - check both main message and failed items
        if (errorData.status === 400 && (
          errorData.message?.toLowerCase().includes("damaged") ||
          errorData.data?.failed?.some(item => item.message?.toLowerCase().includes("damaged"))
        )) {
          const failedShipments = errorData.data?.failed || [];
          const damagedAwbs = failedShipments.map(item => item.awbNumber).join(", ");
          const failedMessages = failedShipments.map(item => item.message).join("\n");

          Modal.warning({
            title: "Cannot Process Some Shipments",
            content: (
              <div>
                <p style={{ marginBottom: 12, fontWeight: 500 }}>
                  We found shipments that cannot be delivered because they are marked as damaged.
                </p>
                {damagedAwbs && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ marginBottom: 4, fontWeight: 500 }}>Shipment Numbers:</p>
                    <p style={{
                      background: '#fff2f0',
                      border: '1px solid #ffccc7',
                      padding: '8px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '12px'
                    }}>
                      {damagedAwbs}
                    </p>
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <p style={{ marginBottom: 4, fontWeight: 500 }}>What to do next:</p>
                  <div style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    <div style={{ marginBottom: '8px' }}>1. Remove the damaged shipments from this list</div>
                    <div style={{ marginBottom: '8px' }}>2. Continue processing the remaining shipments</div>
                  </div>
                </div>
                <p style={{
                  color: '#1890ff',
                  fontSize: '13px',
                  fontWeight: 500,
                  marginBottom: 0,
                  textAlign: 'center',
                  padding: '8px',
                  background: '#f0f9ff',
                  borderRadius: '4px',
                  border: '1px solid #91d5ff'
                }}>
                  👆 Click &quot;Remove from List&quot; button next to the damaged shipment to continue
                </p>
              </div>
            ),
            okText: "Got it",
            width: 520,
            centered: true,
          });
          return;
        }

        // Handle validation errors (array of messages)
        if (Array.isArray(errorData.message)) {
          const validationErrors = errorData.message.join("\n");
          Modal.error({
            title: "Validation Errors",
            content: (
              <div>
                <p>Please fix the following issues:</p>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{validationErrors}</pre>
              </div>
            ),
            okText: "OK",
            width: 500,
          });
          return;
        }

        // Handle other API errors
        if (errorData.message) {
          message.error(errorData.message);
          return;
        }
      }

      // Handle form validation errors
      if (error.errorFields) {
        message.error("Please fill in all required fields");
      } else {
        message.error(error.message || "Failed to update delivery status");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // Table columns for POD upload
  const podUploadTableColumns = [
    {
      title: "AWB Numbers",
      dataIndex: "awbNumbers",
      key: "awbNumbers",
      render: (awbNumbers) => (
        <div className="awb-numbers-cell">
          {awbNumbers.map((awb, index) => (
            <Tag key={index} color="blue" style={{ margin: "2px", fontFamily: 'monospace' }}>
              {awb}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "POD Status",
      key: "podStatus",
      width: 100,
      render: (_, record) => (
        <div className="pod-status-cell" style={{ padding: '4px 0' }}>
          {groupFiles[record.id] ? (
            <div className="status-uploaded">
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginBottom: 4 }}>
                Uploaded
              </Tag>
              <div className="file-info">
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {groupFiles[record.id].file.name}
                </Text>
                <div className="file-actions">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => window.open(groupFiles[record.id].url, "_blank")}
                    style={{ padding: '0 4px', height: 'auto' }}
                  >
                    Preview
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Tag color="warning" style={{ marginBottom: 4 }}>
              Pending Upload
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "uploadPod",
      width: 180,
      render: (_, record) => (
        <div className="upload-pod-cell" style={{ padding: '4px 2px' }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {!groupFiles[record.id] ? (
              // No file uploaded - show upload button that opens modal
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => handleOpenPodModal(record)}
                size="small"
                style={{
                  width: '100%',
                  height: '28px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                Upload POD
              </Button>
            ) : (
              // File uploaded - show delete and replace buttons
              <Space direction="horizontal" size={4} style={{ width: '100%' }}>
                <Button
                  type="default"
                  icon={<DeleteOutlined />}
                  onClick={() => removeGroupFile(record.id)}
                  size="small"
                  danger
                  style={{
                    flex: 1,
                    height: '26px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500',
                    padding: '0 8px'
                  }}
                >
                  Delete POD
                </Button>
                <Upload
                  accept="image/*,application/pdf"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleGroupFileUpload(record.id, file);
                    return false;
                  }}
                  className="replace-upload"
                  style={{ flex: 1 }}
                >
                  <Button
                    type="default"
                    icon={<UploadOutlined />}
                    loading={uploadingFiles[record.id]}
                    size="small"
                    style={{
                      width: '100%',
                      height: '26px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500',
                      padding: '0 8px'
                    }}
                  >
                    {uploadingFiles[record.id] ? 'Uploading...' : 'Replace'}
                  </Button>
                </Upload>
              </Space>
            )}

            {/* Remove Shipment Button - Always visible */}
            <Button
              type="default"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveShipment(record.id)}
              title="Remove this entire shipment from the list"
              style={{
                width: '100%',
                height: '28px',
                borderRadius: '4px',
                borderStyle: 'dashed',
                fontSize: '11px',
                fontWeight: '500',
                padding: '0 8px'
              }}
            >
              Remove from List
            </Button>

            {/* Ungroup Button - Only for grouped shipments */}
            {record.isGroup && (
              <Button
                type="default"
                size="small"
                onClick={() => handleUngroupShipments(record.id)}
                icon={<DeleteOutlined />}
                ghost
                style={{
                  width: '100%',
                  height: '24px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '400',
                  padding: '0 8px'
                }}
              >
                Ungroup
              </Button>
            )}
          </Space>
        </div>
      ),
    },
  ];

  // Table rows selection for grouping
  const groupRowSelection = {
    selectedRowKeys: selectedGroupRows,
    onChange: (selectedKeys) => {
      setSelectedGroupRows(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.isGroup && selectedGroupRows.some(key => {
        const group = shipmentGroups.find(g => g.id === key);
        return group && !group.isGroup;
      }),
    }),
  };

  if (!selectedShipments || selectedShipments.length === 0) {
    return (
      <div className="delivered-pod-upload-container">
        <div className="error-state">
          <ExclamationCircleOutlined
            style={{ fontSize: 48, color: "#faad14" }}
          />
          <Title level={3}>Invalid Access</Title>
          <Text>No shipments selected. Please go back and select shipments.</Text>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="delivered-pod-upload-container">
      {/* Header Section */}
      <div className="pod-upload-header">
        <Row>
          <Col span={24}>
            <div className="header-content">
              <div className="header-left">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                  className="back-button"
                >
                  Back
                </Button>
                <div className="page-title">
                  <Title level={2} style={{ margin: 0 }}>
                    Upload POD Documents
                  </Title>
                  <Text type="secondary">
                    Delivered Status - {selectedShipments.length} Shipment{selectedShipments.length !== 1 ? 's' : ''}
                  </Text>
                </div>
              </div>

              <div className="header-right">
                <Space size="middle" className="header-actions">
                  <div className="upload-info">
                    <Text type="secondary">
                      {Object.keys(groupFiles).length} of {shipmentGroups.length} POD files uploaded
                    </Text>
                    <div className="progress-indicator">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${(Object.keys(groupFiles).length / shipmentGroups.length) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                </Space>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="content-section">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="pod-upload-form"
          initialValues={{
            remarks: remarks,
          }}
        >
          {/* Delivery Details Section */}
          <Card className="delivery-details-card" title="Delivery Details">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="remarks"
                  label="Delivery Remarks"
                >
                  <TextArea
                    placeholder="Enter delivery remarks"
                    autoSize={{ minRows: 3, maxRows: 6 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Bulk Apply Receiver Name Section */}
            <Divider orientation="left">Bulk Apply Receiver Name</Divider>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                <Input
                  placeholder="Enter receiver name to apply to all groups"
                  value={bulkReceiverName}
                  onChange={(e) => setBulkReceiverName(e.target.value)}
                  size="default"
                  onPressEnter={handleBulkApplyReceiverName}
                />
              </Col>
              <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                <Button
                  type="primary"
                  onClick={handleBulkApplyReceiverName}
                  size="default"
                  block
                  disabled={!bulkReceiverName.trim()}
                >
                  Apply to All
                </Button>
              </Col>
              <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                <Button
                  type="default"
                  onClick={handleClearReceiverNames}
                  size="default"
                  block
                  disabled={Object.keys(groupReceiverNames).length === 0}
                  icon={<ClearOutlined />}
                >
                  Clear All
                </Button>
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Apply the same receiver name to all groups, or clear all receiver names and set them individually in the table below.
            </Text>
          </Card>

          {/* Grouping Controls */}
          <Card className="grouping-controls-card">
            <div className="grouping-header">
              <Row gutter={[16, 16]} align="middle" justify="space-between">
                <Col xs={24} sm={24} md={14} lg={16} xl={18}>
                  <div className="grouping-title">
                    <Title level={4} style={{ marginBottom: 8 }}>
                      <GroupOutlined /> POD Upload
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      Upload POD documents for shipments. Group shipments to upload a single POD for multiple AWBs.
                    </Text>
                  </div>
                </Col>
                <Col xs={24} sm={24} md={10} lg={8} xl={6}>
                  <div className="grouping-actions">
                    <Row gutter={[8, 8]} align="middle" justify="end" wrap={false}>
                      <Col>
                        <div className="upload-status-info" style={{ textAlign: 'left', marginRight: 12 }}>
                          <Text type="secondary" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                            {Object.keys(groupFiles).length} of {shipmentGroups.length} POD files uploaded
                          </Text>
                          <div className="progress-indicator" style={{ marginTop: 4 }}>
                            <div
                              className="progress-bar"
                              style={{
                                width: `${(Object.keys(groupFiles).length / shipmentGroups.length) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col>
                        <Space wrap={false} size="small">
                          <Button
                            type={groupingMode ? "default" : "primary"}
                            ghost={!groupingMode}
                            onClick={groupingMode ? handleCloseGrouping : handleOpenGrouping}
                            icon={<GroupOutlined />}
                            size="default"
                          >
                            {groupingMode ? "Close Grouping" : "Group Shipments"}
                          </Button>
                          {groupingMode && selectedGroupRows.length >= 2 && (
                            <Button
                              type="primary"
                              onClick={handleCreateGroup}
                              icon={<FileAddOutlined />}
                              size="default"
                            >
                              Create Group ({selectedGroupRows.length})
                            </Button>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="pod-upload-table">
              <Table
                columns={podUploadTableColumns}
                dataSource={shipmentGroups}
                rowKey="id"
                rowSelection={groupingMode ? groupRowSelection : null}
                pagination={false}
                className="pod-table"
                rowClassName={(record) =>
                  groupFiles[record.id] ? 'row-uploaded' : 'row-pending'
                }
                scroll={{ x: 'max-content' }}
                size="small"
                style={{ marginBottom: 16 }}
              />
            </div>
          </Card>

          {/* Submit Section */}
          <div className="submit-section">
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col xs={24} sm={24} md={16} lg={18} xl={18}>
                <div className="submit-info">
                  <Text type="secondary" className="submit-text">
                    {Object.keys(groupFiles).length === shipmentGroups.length &&
                      shipmentGroups.every(group => groupReceiverNames[group.id]?.trim()) ? (
                      <span className="ready-text">
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                        All POD files uploaded and receiver names provided. Ready to deliver.
                      </span>
                    ) : (
                      <span className="pending-text">
                        <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                        {shipmentGroups.length - Object.keys(groupFiles).length > 0 &&
                          `${shipmentGroups.length - Object.keys(groupFiles).length} POD file(s) pending. `}
                        {shipmentGroups.filter(group => !groupReceiverNames[group.id]?.trim()).length > 0 &&
                          `${shipmentGroups.filter(group => !groupReceiverNames[group.id]?.trim()).length} receiver name(s) missing.`}
                      </span>
                    )}
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={24} md={8} lg={6} xl={6} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitLoading}
                  icon={<CheckCircleOutlined />}
                  disabled={Object.keys(groupFiles).length !== shipmentGroups.length ||
                    !shipmentGroups.every(group => groupReceiverNames[group.id]?.trim())}
                  className="submit-btn"
                  block
                >
                  {submitLoading ? 'Updating Status...' : 'Mark as Delivered'}
                </Button>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      {/* POD Upload Modal */}
      <Modal
        title="Upload POD Document"
        open={podModalVisible}
        onCancel={handlePodModalCancel}
        footer={[
          <Button key="cancel" onClick={handlePodModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={podModalUploading}
            onClick={handlePodModalSubmit}
          >
            Upload POD
          </Button>,
        ]}
        width={500}
        centered
      >
        <Form
          form={podModalForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="receiverName"
            label="Receiver Name"
            rules={[
              { required: true, message: "Please enter receiver name" },
              { min: 2, message: "Name must be at least 2 characters" },
            ]}
          >
            <Input
              placeholder="Enter receiver name"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="POD Document"
            required
          >
            <Upload
              accept="image/*,application/pdf"
              beforeUpload={handlePodModalFileChange}
              showUploadList={false}
              maxCount={1}
            >
              <Button
                icon={<UploadOutlined />}
                size="large"
                style={{ width: '100%' }}
                type={podModalFile ? 'default' : 'dashed'}
              >
                {podModalFile ? `Selected: ${podModalFile.name}` : 'Click to select POD file'}
              </Button>
            </Upload>
            {podModalFile && (
              <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                File size: {(podModalFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}
          </Form.Item>

          {currentGroupForPod && (
            <div style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <Text strong>Shipments:</Text>
              <div style={{ marginTop: 8 }}>
                {currentGroupForPod.awbNumbers.map((awb, index) => (
                  <Tag key={index} color="blue" style={{ margin: '2px', fontFamily: 'monospace' }}>
                    {awb}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveredPODUpload;
